import { useState, useEffect, useMemo, useCallback } from "react";
import { fbList } from "./useFirebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getUser } from "../utils/sessao";
import { toast } from "./useToast";
import { calcularResultado, valorAlvoEfetivo } from "../calculator/resultado";
import { margemPadraoValida } from "../calculator/margem";
import { MARGEM_PADRAO_LS_KEY, VALOR_KWH_PADRAO, PERFIS_TAXAS_PADRAO } from "../calculator/defaults";

const lerMargemPadraoLS = () => { try { return margemPadraoValida(localStorage.getItem(MARGEM_PADRAO_LS_KEY)); } catch { return "50"; } };
const uid4 = () => Math.random().toString(36).slice(2, 9);

/* Hook central de precificação — única fonte de verdade da Calculadora.
   Busca o cadastro (filamentos/impressoras/materiais/perfil de
   precificação), guarda todo o estado de entrada de UM cálculo e deriva o
   `resultado` chamando src/calculator/engine.js a cada mudança. Modo Rápido,
   Modo Avançado e as telas de Registrar Produto/Orçamento leem tudo daqui —
   não existe mais fórmula duplicada em cada tela. */
export function useCalculoProduto() {
  const [filamentos, setFilamentos] = useState([]);
  const [impressoras, setImpressoras] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [carregandoCadastro, setCarregandoCadastro] = useState(true);
  const [precificacao, setPrecificacao] = useState(null); // perfil.precificacao { valorKwh, margemPadrao, perfisTaxas }

  useEffect(() => {
    const u = getUser();
    Promise.all([
      fbList("filamentos"), fbList("impressoras"), fbList("materiais"),
      u?.uid ? getDoc(doc(db, "users", u.uid)).catch(() => null) : Promise.resolve(null),
    ]).then(([fils, imps, mats, perfilSnap]) => {
      setFilamentos(fils);
      setImpressoras(imps);
      setMateriais(mats);
      const cfg = perfilSnap?.exists?.() ? (perfilSnap.data()?.precificacao || null) : null;
      setPrecificacao(cfg);
      const maisUsada = imps.find(i => i.mais_usada);
      if (maisUsada) selecionarImpressora(maisUsada.id, imps);
      setCarregandoCadastro(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valorKwhPadrao = precificacao?.valorKwh ?? VALOR_KWH_PADRAO;
  const margemPadrao = margemPadraoValida(precificacao?.margemPadrao ?? lerMargemPadraoLS());
  const perfisTaxas = precificacao?.perfisTaxas?.length ? precificacao.perfisTaxas : PERFIS_TAXAS_PADRAO;

  // ── Identificação do item calculado ──
  const [nome, setNome] = useState("");
  const [unidades, setUnidades] = useState("1");

  // ── Filamentos usados ──
  const [filamentosCalc, setFilamentosCalc] = useState([]);
  const adicionarFilamento = useCallback((filamentoId, pesoG) => {
    const fil = filamentos.find(f => f.id === filamentoId);
    if (!fil) return;
    const peso = parseFloat(pesoG) || 0;
    if (peso <= 0) { toast("Informe o peso (g) do filamento!", "warning"); return; }
    const custo_g = (parseFloat(fil.custo) || 0) / 1000;
    setFilamentosCalc(v => {
      const existe = v.find(f => f.id === filamentoId);
      if (existe) return v.map(f => f.id === filamentoId ? { ...f, peso: f.peso + peso, custo_total: (f.peso + peso) * custo_g } : f);
      return [...v, { id: fil.id, nome: fil.cor || fil.nome, material: fil.material, custo_g, peso, custo_total: peso * custo_g }];
    });
  }, [filamentos]);
  const removerFilamento = useCallback((id) => setFilamentosCalc(v => v.filter(f => f.id !== id)), []);
  // Adiciona/soma vários de uma vez (usado pela confirmação de cores detectadas num .3mf)
  const adicionarFilamentosEmLote = useCallback((itens) => {
    setFilamentosCalc(v => {
      let novos = [...v];
      for (const { filamentoId, pesoG } of itens) {
        const fil = filamentos.find(f => f.id === filamentoId);
        if (!fil) continue;
        const custo_g = (parseFloat(fil.custo) || 0) / 1000;
        const peso = parseFloat(pesoG) || 0;
        const existe = novos.find(f => f.id === filamentoId);
        if (existe) novos = novos.map(f => f.id === filamentoId ? { ...f, peso: f.peso + peso, custo_total: (f.peso + peso) * custo_g } : f);
        else novos.push({ id: fil.id, nome: fil.cor || fil.nome, material: fil.material, custo_g, peso, custo_total: peso * custo_g });
      }
      return novos;
    });
  }, [filamentos]);

  // ── Materiais extras (com estoque) ──
  const [materiaisCalc, setMateriaisCalc] = useState([]);
  const adicionarMaterial = useCallback((materialId, qtd = 1) => {
    const mat = materiais.find(m => m.id === materialId);
    if (!mat) return;
    const quantidade = parseFloat(qtd) || 1;
    setMateriaisCalc(v => {
      const existe = v.find(m => m.id === materialId);
      if (existe) return v.map(m => m.id === materialId ? { ...m, qtd: m.qtd + quantidade } : m);
      return [...v, { id: mat.id, nome: mat.nome, unidade: mat.unidade, custo_unitario: mat.custo_unitario, qtd: quantidade }];
    });
  }, [materiais]);
  const removerMaterial = useCallback((id) => setMateriaisCalc(v => v.filter(m => m.id !== id)), []);

  // ── Custos adicionais (lista livre, sem estoque: embalagem, cola, pintura, transporte...) ──
  const [custosAdicionais, setCustosAdicionais] = useState([]);
  const adicionarCustoAdicional = useCallback((item) => {
    setCustosAdicionais(v => [...v, { id: uid4(), nome: item.nome || "Custo adicional", tipo: item.tipo || "fixo", valor: parseFloat(item.valor) || 0, quantidade: parseFloat(item.quantidade) || 1 }]);
  }, []);
  const removerCustoAdicional = useCallback((id) => setCustosAdicionais(v => v.filter(i => i.id !== id)), []);

  // ── Tempo de impressão (horas decimais — a tela mostra hrs+min separado) ──
  const [horas, setHoras] = useState("");

  // ── Mão de obra ──
  const [maoObraHrs, setMaoObraHrs] = useState("");
  const [maoObraValorHr, setMaoObraValorHr] = useState("");

  // ── Impressora + energia + desgaste (Simples: R$/h direto da impressora;
  // Avançado: potência×kWh e aquisição/vida-útil/manutenção) ──
  const [impressoraId, setImpressoraId] = useState("");
  const [energiaModo, setEnergiaModo] = useState("simples");
  const [energiaValorHora, setEnergiaValorHora] = useState("");
  const [energiaPotenciaW, setEnergiaPotenciaW] = useState("");
  const [energiaValorKwh, setEnergiaValorKwh] = useState("");
  const [desgasteModo, setDesgasteModo] = useState("simples");
  const [desgasteValorHora, setDesgasteValorHora] = useState("");
  const [desgasteAquisicao, setDesgasteAquisicao] = useState("");
  const [desgasteVidaUtil, setDesgasteVidaUtil] = useState("");
  const [desgasteManutencao, setDesgasteManutencao] = useState("");

  const seedDaImpressora = useCallback((imp) => {
    if (!imp) return;
    setEnergiaValorHora(imp.consumo_hr != null ? String(imp.consumo_hr) : "");
    setDesgasteValorHora(imp.desgaste_hr != null ? String(imp.desgaste_hr) : "");
    // Campos "avançado" só existem em impressoras que já os configuraram —
    // sem eles, o modo simples continua sendo o único disponível pra essa impressora.
    setEnergiaPotenciaW(imp.potencia_w != null ? String(imp.potencia_w) : "");
    setEnergiaValorKwh(imp.valor_kwh != null ? String(imp.valor_kwh) : String(valorKwhPadrao));
    setDesgasteAquisicao(imp.preco != null ? String(imp.preco) : "");
    setDesgasteVidaUtil(imp.vida_util_horas != null ? String(imp.vida_util_horas) : "");
    setDesgasteManutencao(imp.manutencao_total != null ? String(imp.manutencao_total) : "");
  }, [valorKwhPadrao]);

  const selecionarImpressora = useCallback((id, listaImpressoras) => {
    const lista = listaImpressoras || impressoras;
    const imp = lista.find(i => i.id === id);
    setImpressoraId(id);
    seedDaImpressora(imp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressoras]);

  const impressoraAtual = impressoras.find(i => i.id === impressoraId);
  const alternarImpressora = useCallback(() => {
    if (impressoras.length < 2) return;
    const idx = impressoras.findIndex(i => i.id === impressoraId);
    const prox = impressoras[(idx + 1) % impressoras.length];
    selecionarImpressora(prox.id);
  }, [impressoras, impressoraId, selecionarImpressora]);

  // ── Taxas de venda (plataforma, pagamento, comissão, imposto — % sobre o preço) ──
  const [taxas, setTaxas] = useState([]);
  const adicionarTaxa = useCallback((nomeTaxa, pct) => {
    setTaxas(v => [...v, { id: uid4(), nome: nomeTaxa || "Taxa", pct: parseFloat(pct) || 0 }]);
  }, []);
  const removerTaxa = useCallback((id) => setTaxas(v => v.filter(t => t.id !== id)), []);
  const aplicarPerfilTaxas = useCallback((perfilId) => {
    const perfil = perfisTaxas.find(p => p.id === perfilId);
    if (!perfil) return;
    setTaxas(perfil.taxas.map(t => ({ id: uid4(), nome: t.nome, pct: t.pct })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfisTaxas]);

  // ── Objetivo de venda: margem | lucro | preco ──
  const [modo, setModo] = useState("margem");
  const [valorAlvoDigitado, setValorAlvo] = useState(() => lerMargemPadraoLS());
  const valorAlvo = valorAlvoEfetivo({ modo, valorAlvoDigitado, taxas });
  // Lembra a margem como padrão local sempre que o usuário calcula no modo margem —
  // mesmo comportamento que a Calculadora antiga já tinha (localStorage).
  useEffect(() => {
    if (modo !== "margem") return;
    const v = String(valorAlvo).trim();
    if (v !== "") { try { localStorage.setItem(MARGEM_PADRAO_LS_KEY, v); } catch {} }
  }, [modo, valorAlvo]);

  // ── Resultado — recalculado a cada render, sem botão "Calcular" ──
  const resultado = useMemo(() => calcularResultado({
    unidades,
    filamentosCalc, materiaisCalc, custosAdicionais, maoObraHrs, maoObraValorHr, horas,
    energiaModo, energiaValorHora, energiaPotenciaW, energiaValorKwh,
    desgasteModo, desgasteValorHora, desgasteAquisicao, desgasteVidaUtil, desgasteManutencao,
    taxas, modo, valorAlvoDigitado,
  }), [
    unidades,
    filamentosCalc, materiaisCalc, custosAdicionais, maoObraHrs, maoObraValorHr, horas,
    energiaModo, energiaValorHora, energiaPotenciaW, energiaValorKwh,
    desgasteModo, desgasteValorHora, desgasteAquisicao, desgasteVidaUtil, desgasteManutencao,
    taxas, modo, valorAlvoDigitado,
  ]);

  // Foto do estado inteiro deste cálculo (mesmo formato de calcularResultado) e
  // o caminho de volta — usados quando vários arquivos têm um cálculo cada.
  const capturarCalculo = () => ({
    nome, unidades, filamentosCalc, materiaisCalc, custosAdicionais, horas, maoObraHrs, maoObraValorHr,
    impressoraId, energiaModo, energiaValorHora, energiaPotenciaW, energiaValorKwh,
    desgasteModo, desgasteValorHora, desgasteAquisicao, desgasteVidaUtil, desgasteManutencao,
    taxas, modo, valorAlvoDigitado,
  });
  const restaurarCalculo = (s) => {
    setNome(s.nome); setUnidades(s.unidades);
    setFilamentosCalc(s.filamentosCalc); setMateriaisCalc(s.materiaisCalc); setCustosAdicionais(s.custosAdicionais);
    setHoras(s.horas); setMaoObraHrs(s.maoObraHrs); setMaoObraValorHr(s.maoObraValorHr);
    setImpressoraId(s.impressoraId);
    setEnergiaModo(s.energiaModo); setEnergiaValorHora(s.energiaValorHora);
    setEnergiaPotenciaW(s.energiaPotenciaW); setEnergiaValorKwh(s.energiaValorKwh);
    setDesgasteModo(s.desgasteModo); setDesgasteValorHora(s.desgasteValorHora);
    setDesgasteAquisicao(s.desgasteAquisicao); setDesgasteVidaUtil(s.desgasteVidaUtil); setDesgasteManutencao(s.desgasteManutencao);
    setTaxas(s.taxas); setModo(s.modo); setValorAlvo(s.valorAlvoDigitado);
  };

  const uns = Math.max(1, parseInt(unidades) || 1);

  const resetCalculo = useCallback(() => {
    setNome(""); setUnidades("1");
    setFilamentosCalc([]); setMateriaisCalc([]); setCustosAdicionais([]);
    setHoras(""); setMaoObraHrs(""); setMaoObraValorHr("");
    const maisUsada = impressoras.find(i => i.mais_usada);
    if (maisUsada) selecionarImpressora(maisUsada.id);
    else { setImpressoraId(""); setEnergiaValorHora(""); setDesgasteValorHora(""); }
    setTaxas([]);
    setModo("margem"); setValorAlvo(margemPadrao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressoras, margemPadrao, selecionarImpressora]);

  // Salva os novos padrões globais (kWh, margem, perfis de taxas) em
  // users/{uid}.precificacao — aditivo, não migra nada existente.
  const salvarPrecificacaoGlobal = useCallback(async (patch) => {
    const u = getUser();
    if (!u?.uid) return;
    const novo = { ...(precificacao || {}), ...patch };
    try {
      await setDoc(doc(db, "users", u.uid), { precificacao: novo }, { merge: true });
      setPrecificacao(novo);
    } catch (e) { toast("Erro ao salvar: " + e.message, "error"); }
  }, [precificacao]);

  return {
    // cadastro
    filamentos, impressoras, materiais, carregandoCadastro,
    valorKwhPadrao, margemPadrao, perfisTaxas, salvarPrecificacaoGlobal,
    // identificação
    nome, setNome, unidades, setUnidades, uns,
    // filamentos
    filamentosCalc, setFilamentosCalc, adicionarFilamento, removerFilamento, adicionarFilamentosEmLote,
    // materiais extras
    materiaisCalc, setMateriaisCalc, adicionarMaterial, removerMaterial,
    // custos adicionais
    custosAdicionais, setCustosAdicionais, adicionarCustoAdicional, removerCustoAdicional,
    // tempo
    horas, setHoras,
    // mão de obra
    maoObraHrs, setMaoObraHrs, maoObraValorHr, setMaoObraValorHr,
    // impressora / energia / desgaste
    impressoraId, selecionarImpressora, impressoraAtual, alternarImpressora,
    energiaModo, setEnergiaModo, energiaValorHora, setEnergiaValorHora, energiaPotenciaW, setEnergiaPotenciaW, energiaValorKwh, setEnergiaValorKwh,
    desgasteModo, setDesgasteModo, desgasteValorHora, setDesgasteValorHora, desgasteAquisicao, setDesgasteAquisicao, desgasteVidaUtil, setDesgasteVidaUtil, desgasteManutencao, setDesgasteManutencao,
    // taxas
    taxas, setTaxas, adicionarTaxa, removerTaxa, aplicarPerfilTaxas,
    // objetivo
    modo, setModo, valorAlvo, setValorAlvo,
    // resultado
    resultado,
    // ações
    resetCalculo, capturarCalculo, restaurarCalculo,
  };
}
